/**
 * TRETTO - Guide couleur. <tretto-guide-achat> : gamme, format, ajout au panier
 * ([data-ajouter="slug"] ou [data-ajouter="slug:murs,slug:meubles"] pour une association).
 * <tretto-guide-outil> : nuancier, associations, scène, #teinte dans l'adresse.
 * Panier comme product-form.js (#processBatchAddToCart) : CartLinesUpdateEvent, cart/add.js + sections.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const GAMMES = { meubles: 'peinture pour meubles', murs: 'peinture intérieure' };

const norm = (texte = '') => String(texte).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/** Litres d'un format (« 500 ml », « 2,5 L ») pour le tri. */
const litres = (format) => {
  const m = /(\d+(?:[.,]\d+)?)\s*(ml|cl|l)\b/i.exec(format || '');
  return m ? parseFloat(m[1].replace(',', '.')) / ({ ml: 1000, cl: 100 }[m[2].toLowerCase()] || 1) : Infinity;
};

const liste = (noms) => (noms.length < 2 ? noms.join('') : `${noms.slice(0, -1).join(', ')} et ${noms.at(-1)}`);
const mouvement = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

const lire = (selecteur) =>
  [...document.querySelectorAll(selecteur)].flatMap((script) => {
    try {
      return [JSON.parse(script.textContent)];
    } catch {
      return [];
    }
  });

let variantes;
let teintes;

/** Variantes sans doublon : { id, t (teinte), g (gamme), f (format), p (prix), d (dispo), u }. */
const donnees = () =>
  (variantes ??= [...new Map(lire('script[data-tretto-guide-produits]').flatMap((d) => d.v || []).map((v) => [v.id, v])).values()]);

/** Teintes : slug -> { nom, slug, hex, texte }. */
const table = () => (teintes ??= new Map(lire('script[data-tretto-teintes]').flat().map((t) => [t.slug, t])));
const nomDe = (slug) => table().get(slug)?.nom || slug;
const hexDe = (slug) => table().get(slug)?.hex || '';

/** Panier à jour, comme product-form.js (#refreshCart). */
async function lirePanier() {
  const composant = document.querySelector('cart-items-component');
  if (composant) {
    await customElements.whenDefined('cart-items-component');
    return composant.fetchCartData();
  }
  const reponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, { headers: { Accept: 'application/json' } });
  if (!reponse.ok) throw new Error(`Panier illisible (${reponse.status})`);
  return reponse.json();
}

/** Ajout au panier ; prévient le thème (tiroir, compteur). */
async function ajouterAuPanier(source, lignes, ajoutUrl) {
  const sections = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const differe = CartLinesUpdateEvent.createPromise();
  const lines = lignes.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity }));
  source.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: differe.promise }));

  let reponse;
  let panier;
  try {
    const envoi = await fetch(ajoutUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lignes, sections: sections.join(',') }),
    });
    reponse = await envoi.json();
    if (reponse.status) {
      const detail = { description: reponse.description, errors: reponse.errors };
      source.dispatchEvent(new CartErrorEvent({ error: reponse.message || 'Add to cart failed', code: 'INVALID', detail }));
    }
    panier = await lirePanier();
  } catch (erreur) {
    differe.reject(erreur);
    if (!reponse) source.dispatchEvent(new CartErrorEvent({ error: erreur?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    throw erreur;
  }

  const echec = Boolean(reponse.status);
  differe.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
    detail: {
      items: panier.items,
      source: 'tretto-guide',
      sourceId: source.id,
      itemCount: lignes.reduce((n, l) => n + l.quantity, 0),
      sections: echec ? undefined : reponse.sections,
      didError: echec,
    },
  });
  if (echec) throw new Error(reponse.description || reponse.message || '');
}

/**
 * Teintes d'un bouton : data-ajouter="lin:murs,lie-de-vin:meubles" (gamme imposée par le rôle)
 * ou data-ajouter="lie-de-vin" (gamme choisie par le visiteur).
 */
const articles = (bouton) =>
  (bouton.dataset.ajouter || '')
    .split(',')
    .filter(Boolean)
    .map((article) => {
      const [slug, gamme = ''] = article.split(':');
      return { slug, gamme };
    });

const parLitres = (a, b) => litres(a) - litres(b) || a.localeCompare(b, 'fr');

class TrettoGuideAchat extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', (e) => e.target.closest('[data-gamme], [data-format]') && this.maj());
    this.addEventListener('click', (e) => {
      const bouton = e.target.closest('[data-ajouter]');
      if (bouton) this.#ajouter(bouton);
    });
  }

  connectedCallback() {
    variantes = teintes = undefined;
    for (const el of this.querySelectorAll('[data-js]')) el.hidden = false;
    this.maj();
  }

  /** Gamme choisie par le visiteur (boutons sans gamme imposée). */
  get gamme() {
    return this.querySelector('[data-gamme] input:checked')?.value || 'meubles';
  }

  /** Sélecteur de format d'une gamme : [data-format="murs"], sinon le sélecteur commun s'il suit cette gamme. */
  #selecteur(gamme) {
    return this.querySelector(`[data-format="${gamme}"]`) || (gamme === this.gamme ? this.querySelector('[data-format=""]') : null);
  }

  /**
   * Variante disponible d'une teinte. Gamme imposée : format choisi s'il existe, sinon le plus petit format.
   * Gamme du visiteur : uniquement le format choisi.
   */
  variante({ slug, gamme }) {
    const g = gamme || this.gamme;
    const voulu = norm(this.#selecteur(g)?.value ?? '');
    const dispo = donnees().filter((v) => v.t === slug && v.g === g && v.d);
    const exacte = dispo.find((v) => (v.n ??= norm(v.f)) === voulu);
    if (exacte || !gamme) return exacte || null;
    return dispo.sort((a, b) => parLitres(a.f, b.f))[0] || null;
  }

  /** Formats, prix, boutons (sinon lien « Voir »). */
  maj() {
    const boutons = [...this.querySelectorAll('[data-ajouter]')];
    const tous = boutons.flatMap(articles);
    const slugs = new Set(tous.map((a) => a.slug));

    for (const select of this.querySelectorAll('[data-format]')) {
      const imposee = select.dataset.format;
      const g = imposee || this.gamme;
      const concernes = new Set(tous.filter((a) => !imposee || a.gamme === imposee).map((a) => a.slug));
      const formats = new Map();
      for (const v of donnees()) if (v.g === g && concernes.has(v.t)) formats.set(norm(v.f), v.f);
      const choix = [...formats.values()].sort(parLitres);
      if (select.dataset.cle !== choix.join('|')) {
        const avant = norm(select.value);
        select.replaceChildren(...choix.map((f) => new Option(f || 'Format unique', f)));
        select.dataset.cle = choix.join('|');
        const garde = choix.find((f) => norm(f) === avant);
        if (garde !== undefined) select.value = garde;
      }
      const champ = select.closest('[data-format-champ]');
      if (champ) champ.hidden = choix.length < 2;
    }
    const controles = this.querySelector('[data-controles]');
    if (controles) {
      const visibles = [...controles.querySelectorAll('[data-gamme], [data-format-champ]')].some((el) => !el.hidden);
      controles.hidden = !visibles || !donnees().some((v) => slugs.has(v.t));
    }

    for (const bouton of boutons) {
      const demandees = articles(bouton);
      const trouvees = demandees.map((a) => this.variante(a)).filter(Boolean);
      const n = trouvees.length;
      bouton.hidden = !n;
      const libelle = bouton.querySelector('[data-libelle]');
      if (libelle) libelle.textContent = n < demandees.length ? `Ajouter ${n} teinte${n > 1 ? 's' : ''} sur ${demandees.length}` : libelle.dataset.libelle;
      const prix = bouton.querySelector('[data-prix]');
      if (prix) prix.textContent = n ? this.#argent(trouvees.reduce((s, v) => s + v.p, 0)) : '';
      const voir = bouton.parentElement?.querySelector(':scope > [data-voir]');
      if (voir) voir.hidden = n > 0;
    }
  }

  /** Vide les messages d'ajout (changement de teinte). */
  effacerStatuts() {
    for (const zone of this.querySelectorAll('[data-statut]')) zone.replaceChildren();
  }

  async #ajouter(bouton) {
    if (bouton.getAttribute('aria-busy') === 'true') return;
    const trouvees = [];
    const manquantes = [];
    const demandees = articles(bouton);
    for (const article of demandees) {
      const v = this.variante(article);
      if (v) trouvees.push(v);
      else manquantes.push(nomDe(article.slug));
    }
    if (!trouvees.length) return;

    // « Lin et Blanc chaud (peinture intérieure, 1 L), Lie de vin (peinture pour meubles, 500 ml) »
    const groupes = new Map();
    for (const v of trouvees) {
      const cle = `${GAMMES[v.g] || v.g}${v.f ? `, ${v.f}` : ''}`;
      groupes.set(cle, [...(groupes.get(cle) || []), nomDe(v.t)]);
    }
    const detail = [...groupes].map(([cle, noms]) => `${liste(noms)} (${cle})`).join(', ');
    const imposee = demandees.some((a) => a.gamme);

    bouton.setAttribute('aria-busy', 'true');
    bouton.setAttribute('aria-disabled', 'true');
    try {
      await ajouterAuPanier(this, trouvees.map((v) => ({ id: v.id, quantity: 1 })), this.dataset.ajoutUrl);
      let message = `Ajouté au panier : ${detail}.`;
      if (manquantes.length) message += ` ${imposee ? 'Pas disponible pour le moment' : 'Pas disponible dans ce format'} : ${liste(manquantes)}.`;
      this.#statut(message, false, bouton);
      const tiroir = document.querySelector('theme-drawer#cart-drawer');
      if (this.hasAttribute('data-ouvrir-panier') && tiroir && !tiroir.isOpen) tiroir.open?.();
    } catch (erreur) {
      const raison = erreur?.message ? ` : ${erreur.message}` : '';
      this.#statut(`Oups, l'ajout n'a pas fonctionné${raison}. Réessayez dans un instant.`, true, bouton);
    } finally {
      bouton.removeAttribute('aria-busy');
      bouton.removeAttribute('aria-disabled');
    }
  }

  /** Message au plus près du bouton cliqué (carte d'association, carte coup de cœur, zone d'achat). */
  #statut(texte, erreur, bouton) {
    let zone = null;
    for (let n = bouton?.parentElement; !zone && n && this.contains(n); n = n.parentElement) zone = n.querySelector('[data-statut]');
    zone ??= this.querySelector('[data-statut]');
    if (!zone) return;
    this.effacerStatuts();
    zone.classList.toggle('tg-statut--erreur', erreur);
    zone.textContent = `${texte} `;
    if (erreur) return;
    const lien = Object.assign(document.createElement('a'), { href: this.dataset.panierUrl || '/cart', textContent: 'Voir le panier' });
    zone.append(lien);
  }

  #argent(centimes) {
    return formatMoney(centimes, this.dataset.moneyFormat || '{{amount_with_comma_separator}} €', this.dataset.devise || 'EUR');
  }
}

class TrettoGuideOutil extends HTMLElement {
  #controle;
  #pastilles = [];

  connectedCallback() {
    this.#controle = new AbortController();
    const { signal } = this.#controle;
    this.#pastilles = [...this.querySelectorAll('[data-teinte]')];
    this.querySelector('[data-nuancier]')?.addEventListener('keydown', this.#clavier, { signal });
    this.addEventListener('click', this.#auClic, { signal });
    document.addEventListener('click', this.#lienVersTeinte, { signal });
    window.addEventListener('hashchange', () => this.#depuisAdresse(), { signal });
    if (!this.#depuisAdresse()) this.choisir(this.dataset.defaut || '', { adresse: false, annonce: false });
  }

  disconnectedCallback() {
    this.#controle?.abort();
  }

  /** Affiche une teinte : pastille, panneau, achat, scène, adresse. */
  choisir(slug, { adresse = true, annonce = true } = {}) {
    const panneau = slug && this.querySelector(`[data-panneau="${CSS.escape(slug)}"]`);
    if (!panneau) return false;

    for (const b of this.#pastilles) b.setAttribute('aria-pressed', String(b.dataset.teinte === slug));
    for (const p of this.querySelectorAll('[data-panneau]')) p.hidden = p !== panneau;

    const nom = nomDe(slug);
    const zone = this.querySelector('.tg-achat');
    const bouton = zone?.querySelector('[data-ajouter]');
    if (bouton) bouton.dataset.ajouter = slug;
    for (const el of this.querySelectorAll('[data-achat-nom]')) el.textContent = nom;
    this.querySelector('[data-achat-pastille]')?.style.setProperty('--tg-c', hexDe(slug));
    const voir = zone?.querySelector('[data-voir]');
    if (voir) voir.search = `?${new URLSearchParams({ type: 'product', q: nom })}`;
    const achat = this.querySelector('tretto-guide-achat');
    achat?.effacerStatuts?.();
    achat?.maj?.();

    const active = panneau.querySelector('[data-scene][aria-pressed="true"]') || panneau.querySelector('[data-scene]');
    if (active) this.#montrer(active, false);
    else this.#peindre([slug]);

    if (adresse) history.replaceState(history.state, '', `#${slug}`);
    if (annonce) {
      const n = panneau.querySelectorAll('.tg-asso').length;
      const s = n > 1 ? 's' : '';
      this.#annoncer(`${nom}\u00a0: ${n} association${s} conseillée${s}.`);
    }
    return true;
  }

  /** Teinte de l'adresse (#vert-foret, #Vert%20forêt…). */
  #depuisAdresse() {
    let ancre = '';
    try {
      ancre = decodeURIComponent(location.hash.slice(1));
    } catch {}
    const slug = norm(ancre).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!this.choisir(slug, { adresse: false })) return false;
    requestAnimationFrame(() => this.scrollIntoView({ behavior: mouvement(), block: 'start' }));
    return true;
  }

  #auClic = (event) => {
    const pastille = event.target.closest('[data-teinte]');
    if (pastille) return void this.choisir(pastille.dataset.teinte);
    const scene = event.target.closest('[data-scene]');
    if (scene) this.#montrer(scene, true);
  };

  /** Lien [data-tg-lien-teinte="slug"] ou « #slug » ailleurs dans la page : ouvre l'outil sur la teinte. */
  #lienVersTeinte = (event) => {
    const lien = event.target.closest?.('a[data-tg-lien-teinte], a[href^="#"]');
    if (!lien || this.contains(lien)) return;
    const slug = lien.dataset.tgLienTeinte || lien.getAttribute('href').slice(1);
    if (!this.choisir(slug)) return;
    event.preventDefault();
    this.scrollIntoView({ behavior: mouvement(), block: 'start' });
    this.querySelector(`[data-teinte="${CSS.escape(slug)}"]`)?.focus({ preventScroll: true });
  };

  /** Flèches, Début et Fin pour parcourir le nuancier. */
  #clavier = (event) => {
    const i = this.#pastilles.indexOf(document.activeElement);
    if (i < 0) return;
    const colonnes = getComputedStyle(event.currentTarget).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    const fin = this.#pastilles.length - 1;
    const sauts = { ArrowRight: i + 1, ArrowLeft: i - 1, ArrowDown: i + colonnes, ArrowUp: i - colonnes, Home: 0, End: fin };
    if (!(event.key in sauts)) return;
    event.preventDefault();
    this.#pastilles[Math.max(0, Math.min(fin, sauts[event.key]))].focus();
  };

  /** « Voir en situation ». */
  #montrer(bouton, depuisClic) {
    for (const b of bouton.closest('[data-panneau]')?.querySelectorAll('[data-scene]') || []) {
      b.setAttribute('aria-pressed', String(b === bouton));
    }
    const slugs = bouton.dataset.scene.split(',').filter(Boolean);
    this.#peindre(slugs);
    if (!depuisClic) return;
    const zone = this.querySelector('[data-scene-zone]');
    const cadre = zone?.getBoundingClientRect();
    if (cadre && (cadre.top < 0 || cadre.bottom > innerHeight)) zone.scrollIntoView({ behavior: mouvement(), block: 'nearest' });
    this.#annoncer(`Mise en situation\u00a0: ${slugs.map(nomDe).join(', ')}.`);
  }

  /** Scène : murs, meuble, touche, plafond. */
  #peindre(slugs) {
    const zone = this.querySelector('[data-scene-zone]');
    if (!zone || !slugs.length) return;
    const [mur, meuble = mur, touche = meuble, plafond = 'blanc-chaud'] = slugs;
    const couleurs = { mur, meuble, touche, plafond };
    for (const role in couleurs) zone.style.setProperty(`--tg-${role}`, hexDe(couleurs[role]));

    const parts = { 1: [100], 2: [70, 30], 3: [60, 30, 10] }[slugs.length] || [45, 30, 10, 15];
    zone.querySelectorAll('[data-role]').forEach((role, i) => {
      role.hidden = !slugs[i];
      if (!slugs[i]) return;
      role.querySelector('[data-role-nom]').textContent = nomDe(slugs[i]);
      role.querySelector('[data-role-part]').textContent = parts[i];
      role.querySelector('[data-role-pastille]').style.setProperty('--tg-c', hexDe(slugs[i]));
    });
  }

  #annoncer(texte) {
    const zone = this.querySelector('[data-annonce]');
    zone?.replaceChildren();
    requestAnimationFrame(() => zone?.replaceChildren(texte));
  }
}

if (!customElements.get('tretto-guide-achat')) customElements.define('tretto-guide-achat', TrettoGuideAchat);
if (!customElements.get('tretto-guide-outil')) customElements.define('tretto-guide-outil', TrettoGuideOutil);
